import last from 'lodash/last';
import cloneDeep from 'lodash/cloneDeep';
import { TILE_COLORS, TileColor } from '../../TileColor';
import MathUtils from '../../../../utils/MathUtils';
import type { TrayRefillSettings } from './TrayRefillRandomizer';
import type { BoardModel } from '../board/BoardModel';
import type { TileStackModel } from '../board/TileStackModel';
import { BOARD_SETTINGS } from '../../../scene/GameSceneConstants';
import { shuffle } from '../../../../utils/array-utils';

interface TrayMatchChanceOverrideConfig {
  refills: TrayRefillSettings[];
  boardModel: BoardModel;
}

type CandidateWithMatchValues = { stack: TileStackModel; matchValues: Record<TileColor, number> };

export class TrayMatchChanceOverride {
  public static debugChancesOverride: { matchChance: number; fullMatchChance: number } | null = null;

  private levelProgress = 0;

  constructor(private readonly config: TrayMatchChanceOverrideConfig) {}

  public updateLevelProgress(levelProgress: number): void {
    this.levelProgress = levelProgress;
  }

  private getFreeSpacesWithNeighbours(): TileStackModel[] {
    return this.config.boardModel
      .getEmptyStacks()
      .filter((s) => s.getConnectedStacks().some((neighbour) => !neighbour.blocker && !neighbour.isEmpty()));
  }

  private getCandidatesWithMatchValues(): CandidateWithMatchValues[] {
    return this.getFreeSpacesWithNeighbours().map((stack) => ({
      stack,
      matchValues: this.config.boardModel.getMatchValuesForStack(stack),
    }));
  }

  private isFullMatchOverrideNecessary(colors: TileColor[][], candidates: CandidateWithMatchValues[]): boolean {
    const colorsToMatch = this.getTrayStackColorsToMatchAmounts(colors);

    const matchingStack = candidates.find(({ matchValues }) =>
      colorsToMatch.some(({ color, amount }) => matchValues[color] + amount[0] >= BOARD_SETTINGS.matchLength),
    );

    return !matchingStack;
  }

  private getTrayStackColorsToMatchAmounts(colors: TileColor[][]): { color: TileColor; amount: number[] }[] {
    return colors.map((stackColors) => {
      const color = last(stackColors) ?? TileColor.None;
      const amount = [];

      let currentColor = color;
      let currentAmount = 0;

      for (let i = stackColors.length - 1; i >= 0; i -= 1) {
        if (stackColors[i] !== currentColor) {
          amount.push(currentAmount);
          currentAmount = 1;
          currentColor = stackColors[i];
        } else {
          currentAmount += 1;
        }
      }

      if (currentAmount > 0) {
        amount.push(currentAmount);
      }

      return { color, amount };
    });
  }

  private isPartialMatchOverrideNecessary(colors: TileColor[][]): boolean {
    const freeSpacesWithNeighbours = this.getFreeSpacesWithNeighbours();

    const colorsToMatch = colors.map((c) => last(c) ?? TileColor.None);

    const hasTargetToMove = freeSpacesWithNeighbours.some((stack) => {
      const topColorsOfNeighbours = stack
        .getConnectedStacks()
        .filter((s) => !s.blocker && !s.isEmpty())
        .map((s) => s.getTopmostColor());
      return colorsToMatch.some((c) => topColorsOfNeighbours.includes(c));
    });

    return !hasTargetToMove;
  }

  private performPartialMatchOverride(colors: TileColor[][], candidates: CandidateWithMatchValues[]): TileColor[][] {
    shuffle(candidates);

    if (candidates.length === 0) return colors;

    const colorToUse = shuffle([...TILE_COLORS]).find((potentialColor) =>
      candidates.some((candidate) => candidate.matchValues[potentialColor] > 0),
    );

    if (!colorToUse) return colors;

    const colorStackToOverride = MathUtils.rnd.pick(colors);

    this.log(
      `performing partial match override in stack #${colors.indexOf(colorStackToOverride)}, changing color from ${last(colorStackToOverride)} to ${colorToUse}`,
    );
    this.replaceLastColor(colorStackToOverride, colorToUse);

    return colors;
  }

  private replaceLastColor(colorStack: TileColor[], colorToUse: TileColor): TileColor[] {
    const colorToReplace = last(colorStack) ?? TileColor.None;
    for (let i = colorStack.length - 1; i >= 0; i -= 1) {
      if (colorStack[i] !== colorToReplace) break;
      colorStack[i] = colorToUse;
    }
    return colorStack;
  }

  private performFullMatchOverride(colors: TileColor[][], candidates: CandidateWithMatchValues[]): TileColor[][] {
    const trayMatchAmounts = this.getTrayStackColorsToMatchAmounts(colors);

    shuffle(candidates);

    const potentialChanges: { colorToApply: TileColor; colorStackIndex: number; tilesToChange: number }[] = [];

    candidates.forEach((candidate) => {
      const colorWithHighestMatchValue = shuffle([...TILE_COLORS]).reduce((acc, cur) =>
        candidate.matchValues[cur] > candidate.matchValues[acc] ? cur : acc,
      );

      trayMatchAmounts.forEach(({ amount }, colorStackIndex) => {
        let currentAmount = 0;
        for (let i = 0; i < amount.length; i += 1) {
          currentAmount += amount[i];
          if (candidate.matchValues[colorWithHighestMatchValue] + currentAmount >= BOARD_SETTINGS.matchLength) {
            potentialChanges.push({
              colorToApply: colorWithHighestMatchValue,
              colorStackIndex,
              tilesToChange: currentAmount,
            });
          }
        }
      });
    });

    const smallestAmountToChange = potentialChanges.reduce(
      (acc, potentialChange) => Math.min(acc, potentialChange.tilesToChange),
      Infinity,
    );

    const finalChange = shuffle(
      potentialChanges.filter(({ tilesToChange }) => tilesToChange === smallestAmountToChange),
    )[0];

    this.log({ colors: cloneDeep(colors), smallestAmountToChange, finalChange, potentialChanges, trayMatchAmounts });

    if (!finalChange) return this.performPartialMatchOverride(colors, candidates);

    this.log(
      `performing full match override in stack #${finalChange.colorStackIndex}, changing color from ${trayMatchAmounts[finalChange.colorStackIndex].color} to ${finalChange.colorToApply}, tiles affected: ${finalChange.tilesToChange}`,
    );

    colors[finalChange.colorStackIndex] = colors[finalChange.colorStackIndex].map((originalColor, index, array) => {
      if (index >= array.length - finalChange.tilesToChange) return finalChange.colorToApply;
      return originalColor;
    });

    return colors;
  }

  private getMatchChances(): { matchChance: number; fullMatchChance: number } | null {
    if (TrayMatchChanceOverride.debugChancesOverride) {
      return TrayMatchChanceOverride.debugChancesOverride;
    }

    const { boardModel, refills } = this.config;

    const emptyStacksCount = boardModel.getEmptyStackCount();

    return (
      refills
        .find(({ scoreRange }) => MathUtils.isBetween(this.levelProgress, scoreRange[0], scoreRange[1]))
        ?.freeSpaceMatchChance?.find(({ freeSpaces }) =>
          MathUtils.isBetween(emptyStacksCount, freeSpaces.min, freeSpaces.max),
        ) ?? null
    );
  }

  public performOverride(colors: TileColor[][]): TileColor[][] {
    const matchChances = this.getMatchChances();

    if (!matchChances) return colors;

    this.log('got match chance config', matchChances);

    const rnd = Math.random();

    if (rnd > matchChances.fullMatchChance + matchChances.matchChance) {
      this.log(`RND: ${rnd}, no override`);
      return colors;
    }

    const fullMatch = rnd < matchChances.fullMatchChance;

    this.log(`RND: ${rnd}, ${fullMatch ? 'full match override' : 'partial match override'}`);

    const candidates = this.getCandidatesWithMatchValues();

    if (candidates.length === 0) {
      this.log('no candidates for match');
      return colors;
    }

    if (fullMatch) {
      if (!this.isFullMatchOverrideNecessary(colors, candidates)) {
        this.log('no full match override necessary');
        return colors;
      }
      return this.performFullMatchOverride(colors, candidates);
    }
    if (!this.isPartialMatchOverrideNecessary(colors)) {
      this.log('no partial match override necessary');
      return colors;
    }
    return this.performPartialMatchOverride(colors, candidates);
  }

  private log(...args: unknown[]): void {
    args.forEach(() => {});
    // console.warn('MATCH CHANCE OVERRIDE', ...args);
  }
}
